import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import VenueGalleryManager from './VenueGalleryManager';
import VenueFeaturesManager from './VenueFeaturesManager';
import VenueBlogsManager from './VenueBlogsManager';
import VenueMenuManager from './VenueMenuManager';
import ControlTowerMap from './child/ControlTowerMap';
import { formatDisplayDate } from '../utils/formatDate';

const VenueDetailLayer = ({ venueId }) => {
    const { auth, user } = useAuth();
    const navigate = useNavigate();
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const galleryVisited = useRef(false);
    if (activeTab === 'gallery') galleryVisited.current = true;
    const [selectedVouchers, setSelectedVouchers] = useState([]);
    const [savingVouchers, setSavingVouchers] = useState(false);

    const fetchVenueData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`);
        
            if (res.ok) {
                const venueData = await res.json();
                setVenue(venueData);

                if (Array.isArray(venueData.vouchers)) {
                    setSelectedVouchers(venueData.vouchers);
                } else if (venueData.vouchers) {
                    try {
                        const parsed = typeof venueData.vouchers === 'string'
                            ? JSON.parse(venueData.vouchers)
                            : venueData.vouchers;
                        setSelectedVouchers(Array.isArray(parsed) ? parsed : []);
                    } catch (e) {
                        setSelectedVouchers([]);
                    }
                } else {
                    setSelectedVouchers([]);
                }
            } else {
                toast.error('Venue not found');
                navigate(user?.role === 'admin' ? '/venue-verification' : '/my-venues');
            }
        } catch (error) {
            console.error('Error fetching venue:', error);
            toast.error('Failed to load venue');
        } finally {
            setLoading(false);
        }
    }, [auth, venueId, navigate, user?.role]);

    useEffect(() => {
        if (venueId) {
            fetchVenueData();
        }
    }, [venueId, fetchVenueData]);

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { cls: 'hs-badge-warning', icon: 'solar:clock-circle-bold', label: 'Pending Review' },
            approved: { cls: 'hs-badge-success', icon: 'solar:check-circle-bold', label: 'Approved' },
            rejected: { cls: 'hs-badge-danger', icon: 'solar:close-circle-bold', label: 'Rejected' },
            suspended: { cls: 'hs-badge-neutral', icon: 'solar:danger-triangle-bold', label: 'Suspended' }
        };
        const s = statusMap[status] || statusMap.pending;
        return (
            <span className={`hs-badge ${s.cls}`}>
                <Icon icon={s.icon} className="text-sm" />
                {s.label}
            </span>
        );
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: 'solar:info-circle-bold' },
        { id: 'gallery', label: 'Gallery', icon: 'solar:gallery-bold' },
        { id: 'features', label: 'Features', icon: 'solar:star-bold' },
        { id: 'blogs', label: 'Blog Posts', icon: 'solar:document-text-bold' },
        { id: 'menu', label: 'Menu Items', icon: 'solar:chef-hat-bold' },
        { id: 'vouchers', label: 'Vouchers & Deals', icon: 'solar:ticket-sale-bold' }
    ];

    const vouchers = [
        {
            id: 'fazaa',
            name: 'Fazaa',
            image: '/assets/images/vouchers/fazaa.png',
            url: 'https://www.fazaa.ae/'
        },
        {
            id: 'esaad',
            name: 'Esaad',
            image: '/assets/images/vouchers/esaad.png',
            url: 'https://esaad.dubaipolice.gov.ae/home'
        },
        {
            id: 'entertainer',
            name: 'The ENTERTAINER',
            image: '/assets/images/vouchers/theentertainerme.png',
            url: 'https://www.theentertainerme.com/'
        }
    ];

    const toggleVoucher = (id) => {
        setSelectedVouchers((prev) =>
            prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
        );
    };

    const handleSaveVouchers = useCallback(async () => {
        if (!venueId) return;
        setSavingVouchers(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ vouchers: selectedVouchers })
            });

            let data = {};
            try {
                data = await response.json();
            } catch (e) {
                data = {};
            }

            if (response.ok) {
                toast.success('Vouchers updated successfully');
            } else {
                toast.error(data.message || 'Failed to update vouchers');
            }
        } catch (error) {
            console.error('Error updating vouchers:', error);
            toast.error('Failed to update vouchers');
        } finally {
            setSavingVouchers(false);
        }
    }, [auth, venueId, selectedVouchers]);

    if (loading) {
        return (
            <div className="hs-card p-0">
                <div className="hs-card-body d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading venue details...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!venue) {
        return (
            <div className="hs-card p-0">
                <div className="hs-card-body hs-empty-state">
                    <div className="hs-empty-icon">
                        <Icon icon="solar:shop-2-bold" />
                    </div>
                    <h5 className="hs-empty-title">Venue Not Found</h5>
                    <p className="hs-empty-desc">The requested venue could not be loaded or may have been deleted.</p>
                    <Link to={user?.role === 'admin' ? "/venue-verification" : "/my-venues"}
                                    state={{ openVenueId: venue.id }} className="hs-btn-primary">
                        <Icon icon="solar:arrow-left-bold" />
                        {user?.role === 'admin' ? "Back to Verification Center" : "Back to My Venues"}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Venue Hero Header Card */}
            <div className="hs-card mb-20">
                <div className="hs-card-body p-24">
                    <div className="row align-items-center gy-3">
                        <div className="col-auto">
                            {venue.logo_url ? (
                                <img 
                                    src={venue.logo_url} 
                                    alt={venue.title}
                                    className="w-80-px h-80-px rounded-16 object-fit-cover shadow-sm border"
                                />
                            ) : (
                                <div className="w-80-px h-80-px rounded-16 bg-primary-50 text-primary-600 border d-flex align-items-center justify-content-center">
                                    <Icon icon="solar:shop-bold" className="text-3xl" />
                                </div>
                            )}
                        </div>
                        <div className="col">
                            <div className="d-flex align-items-center gap-12 mb-6 flex-wrap">
                                <h4 className="mb-0 fw-bold text-dark">{venue.title || venue.business_name}</h4>
                                {getStatusBadge(venue.verification_status || 'pending')}
                            </div>
                            <p className="text-secondary-light text-sm mb-12 d-flex align-items-center gap-1">
                                <Icon icon="solar:map-point-bold" className="text-primary-600 text-base" />
                                {venue.address_street && `${venue.address_street}, `}
                                {venue.address_city && `${venue.address_city}, `}
                                {venue.address_country || venue.address || 'No address provided'}
                            </p>
                            <div className="d-flex gap-8 flex-wrap">
                                <span className="hs-chip">
                                    <Icon icon="solar:gallery-bold" className="text-primary-600" />
                                    {venue.gallery?.length || 0} Media Files
                                </span>
                                <span className="hs-chip">
                                    <Icon icon="solar:star-bold" className="text-warning-600" />
                                    {venue.features?.length || 0} Amenities
                                </span>
                                <span className="hs-chip">
                                    <Icon icon="solar:document-text-bold" className="text-info-600" />
                                    {venue.blogs?.length || 0} Feed Posts
                                </span>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="d-flex align-items-center gap-10">
                                <button
                                    className="hs-btn-outline-primary hs-btn-sm"
                                    onClick={() => navigate(`/edit-venue/${venue.id}`)}
                                >
                                    <Icon icon="solar:pen-bold" />
                                    Edit Details
                                </button>
                                <Link 
                                    to={user?.role === 'admin' ? "/venue-verification" : "/my-venues"} 
                                    className="hs-btn-secondary hs-btn-sm text-decoration-none"
                                >
                                    <Icon icon="solar:arrow-left-bold" />
                                    {user?.role === 'admin' ? "Back to Verification Center" : "Back to Venues"}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Segmented Navigation Tabs */}
            <div className="hs-card mb-24 p-8 d-flex justify-content-center align-items-center">
                <div className="hs-nav-pills d-flex flex-wrap justify-content-center align-items-center gap-1 mx-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`hs-nav-link d-inline-flex align-items-center justify-content-center gap-2 ${activeTab === tab.id ? 'active shadow-xs' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                            type="button"
                        >
                            <Icon icon={tab.icon} className="text-base text-primary-600 flex-shrink-0" style={{ color: '#0789d5' }} />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="row g-24">
                    {/* Description */}
                    <div className="col-lg-8">
                        <div className="hs-card h-100">
                            <div className="hs-card-header">
                                <h6 className="mb-0 fw-bold text-dark">About This Venue</h6>
                            </div>
                            <div className="hs-card-body">
                                <p className="text-secondary-light line-height-1-8 mb-0">
                                    {venue.description || 'No description provided.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Quick Info */}
                    <div className="col-lg-4">
                        <div className="hs-card h-100">
                            <div className="hs-card-header">
                                <h6 className="mb-0 fw-bold text-dark">Quick Details</h6>
                            </div>
                            <div className="hs-card-body">
                                <ul className="list-unstyled mb-0">
                                    <li className="d-flex justify-content-between py-10 border-bottom">
                                        <span className="text-secondary-light text-sm">Created</span>
                                        <span className="fw-semibold text-dark text-sm">
                                            {venue.created_at ? formatDisplayDate(venue.created_at) : 'N/A'}
                                        </span>
                                    </li>
                                    <li className="d-flex justify-content-between py-10 border-bottom">
                                        <span className="text-secondary-light text-sm">Last Updated</span>
                                        <span className="fw-semibold text-dark text-sm">
                                            {venue.updated_at ? formatDisplayDate(venue.updated_at) : 'N/A'}
                                        </span>
                                    </li>
                                    {venue.license_number && (
                                        <li className="d-flex justify-content-between py-10 border-bottom">
                                            <span className="text-secondary-light text-sm">Trade License</span>
                                            <span className="fw-semibold text-dark text-sm">{venue.license_number}</span>
                                        </li>
                                    )}
                                    {venue.phone && (
                                        <li className="d-flex justify-content-between py-10 border-bottom">
                                            <span className="text-secondary-light text-sm">Phone</span>
                                            <span className="fw-semibold text-dark text-sm">{venue.phone}</span>
                                        </li>
                                    )}
                                    {venue.email && (
                                        <li className="d-flex justify-content-between py-10">
                                            <span className="text-secondary-light text-sm">Email</span>
                                            <span className="fw-semibold text-dark text-sm">{venue.email}</span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Address Details */}
                    <div className="col-12">
                        <div className="hs-card">
                            <div className="hs-card-header">
                                <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                    <Icon icon="solar:map-point-bold" className="text-primary-600" />
                                    Location & Physical Address
                                </h6>
                            </div>
                            <div className="hs-card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="mb-12">
                                            <label className="text-secondary-light text-xs fw-semibold text-uppercase">Street Address</label>
                                            <p className="fw-medium text-dark mb-0">{venue.address_street || venue.address || 'Not provided'}</p>
                                        </div>
                                        <div className="mb-12">
                                            <label className="text-secondary-light text-xs fw-semibold text-uppercase">City</label>
                                            <p className="fw-medium text-dark mb-0">{venue.address_city || 'Not provided'}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-12">
                                            <label className="text-secondary-light text-xs fw-semibold text-uppercase">State / Province</label>
                                            <p className="fw-medium text-dark mb-0">{venue.address_state || 'Not provided'}</p>
                                        </div>
                                        <div className="mb-12">
                                            <label className="text-secondary-light text-xs fw-semibold text-uppercase">Country</label>
                                            <p className="fw-medium text-dark mb-0">{venue.address_country || 'Not provided'}</p>
                                        </div>
                                    </div>
                                </div>
                                {(venue.location || (venue.latitude && venue.longitude)) && (
                                    <div className="mt-16 p-12 bg-neutral-50 rounded-8 border">
                                        <label className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-4">Geographic Coordinates</label>
                                        <code className="text-primary-700">
                                            Lat: {venue.location?.coordinates?.[1] || venue.latitude || 'N/A'}, 
                                            Lng: {venue.location?.coordinates?.[0] || venue.longitude || 'N/A'}
                                        </code>
                                    </div>
                                )}
                                <div className="mt-20">
                                    <ControlTowerMap
                                        venues={[venue]}
                                        height="380px"
                                        title={`${venue.title || venue.business_name || 'Venue'} Map Location`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Opens from the venue data already loaded, then stays mounted */}
            {galleryVisited.current && (
                <div className={activeTab === 'gallery' ? '' : 'd-none'}>
                    <VenueGalleryManager
                        key={venueId}
                        venueId={venueId}
                        initialImages={venue?.gallery}
                        initialLimit={venue?.limits?.galleryLimit}
                    />
                </div>
            )}

            {activeTab === 'features' && (
                    <VenueFeaturesManager venueId={venueId} />
                )}

                {activeTab === 'blogs' && (
                    <VenueBlogsManager 
                        venueId={venueId} 
                        isOwner={user && venue && (user.id === venue.owner_id || user.role === 'admin')}
                    />
                )}

                {activeTab === 'menu' && (
                <VenueMenuManager venueId={venueId} />
            )}

            {activeTab === 'vouchers' && (
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Vouchers & Deals</h6>
                        <div className="d-flex align-items-center gap-12">
                            <span className="text-secondary-light text-sm">
                                Hover over a card to preview the partner link.
                            </span>
                            <button
                                type="button"
                                className="btn btn-primary-600 btn-sm"
                                onClick={handleSaveVouchers}
                                disabled={savingVouchers}
                            >
                                {savingVouchers ? 'Saving...' : 'Save Selection'}
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row g-24">
                            {vouchers.map((voucher) => (
                                <div className="col-md-4" key={voucher.id}>
                                    <label
                                        className={`d-block border radius-12 p-16 h-100 cursor-pointer ${
                                            selectedVouchers.includes(voucher.id)
                                                ? 'border-primary-600 bg-primary-50'
                                                : 'border-neutral-200'
                                        }`}
                                        title={voucher.url}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={selectedVouchers.includes(voucher.id)}
                                                    onChange={() => toggleVoucher(voucher.id)}
                                                />
                                                <span className="ms-8 fw-medium">{voucher.name}</span>
                                            </div>
                                        </div>
                                        <div className="text-center mb-12">
                                            <img
                                                src={voucher.image}
                                                alt={voucher.name}
                                                className="img-fluid"
                                                style={{ maxHeight: '80px', objectFit: 'contain' }}
                                            />
                                        </div>
                                        <div className="text-center">
                                            <a
                                                href={voucher.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary-600 text-sm"
                                            >
                                                {voucher.url}
                                            </a>
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VenueDetailLayer;
